/*
 * PASS-943 Modal components taken from @rally/connect-components (a.k.a telemedicine-components)
 * adjusted for RDS-based styling
 */
import React, { ComponentType, HTMLAttributes, ReactNode } from 'react';
import styled from 'styled-components';

// eslint-disable-next-line no-restricted-imports
import { ParsedContent } from '@pass-frontend/common/components/parsed-content/ui';
import { Text } from '@pass-frontend/common/components/typography/ui';
import { MEDIA_QUERY } from '@pass-frontend/utility/style-helpers';

type HeaderHTMLAttributes = HTMLAttributes<HTMLDivElement>;

export interface Props<
  Heading = string | ComponentType<{ id: Props['ariaHeadingId'] }> | ReactNode,
> extends HeaderHTMLAttributes {
  heading?: Heading;
  ariaLabel?: string | Heading;
  subHeading?: string | ReactNode;
  className?: string;
  ariaHeadingId?: string;
  shouldHideHeading?: boolean;
}

/**
 * Excludes React components and render props
 */
const shouldUseParsedContent = (value: Props['heading']): boolean =>
  typeof value === 'string' || !value || React.isValidElement(value);

const HeaderWithStyledText = (props: Props) => <Text tag="header" {...props} />;

const Header = styled(HeaderWithStyledText)`
  align-items: flex-start;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  padding: 32px 104px;
  position: relative;
  text-align: center;

  ${MEDIA_QUERY.mobile} {
    padding: 64px 16px 16px;
  }

  section {
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin: 0 auto;
    min-height: 40px; /* to ensure height at least for close button */
    width: 100%; /* for IE */
  }
`;

export const ModalHeader = (props: Props) => {
  const {
    heading,
    subHeading,
    ariaHeadingId,
    children,
    shouldHideHeading = false,
    ...htmlAttributes
  } = props;

  return (
    !shouldHideHeading && (
      <Header {...htmlAttributes}>
        <section>
          {shouldUseParsedContent(heading) ? (
            <ParsedContent
              tag="h2"
              potentiallyDangerous
              id={ariaHeadingId}
              className={`${shouldHideHeading ? 'is-sr-only' : 'is-rds-h2'}`}
            >
              {heading}
            </ParsedContent>
          ) : (
            !!heading &&
            React.createElement(
              heading as ComponentType<{ id: Props['ariaHeadingId'] }>,
              { id: ariaHeadingId },
            )
          )}
          <ParsedContent tag="h3" potentiallyDangerous>
            {subHeading}
          </ParsedContent>
        </section>
        {children}
      </Header>
    )
  );
};
